import { type ColDef, type GetRowIdParams, type ValueGetterParams } from 'ag-grid-community';
import { type CustomCellRendererProps } from 'ag-grid-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { GroupType } from '@treams/treams-network';
import { Card } from '@treams/treams-ui/common/Card';
import { DataGrid } from '@treams/treams-ui/common/DataGrid';
import { Tabs } from '@treams/treams-ui/common/Tabs';
import { Heading3 } from '@treams/treams-ui/common/Text';

import { ratio } from '../cycles/CycleAnalytics';
import { GridProgressCell } from '../cycles/components/GridProgressCell';
import { type TalentReviewDepartmentRow } from './useTalentReviewAnalytics';

type GroupTab = GroupType.Department | GroupType.Team;

const TAB_LABEL_KEYS: Record<GroupTab, string> = {
	[GroupType.Department]: 'analytics.treamsScore.column.departments',
	[GroupType.Team]: 'analytics.treamsScore.column.teams',
};

export type TalentReviewTableRow = TalentReviewDepartmentRow & {
	type: GroupType;
};

// The grid grows with its rows, same as the cycle analytics tables. Without
// this, AG Grid keeps its own vertical scrollbar and the content pane scrolls too.
export const GridClip = styled.div`
	min-width: 0;
	width: 100%;
	max-width: 100%;
	position: relative;
	z-index: 0;
	contain: layout;

	&& .ag-layout-auto-height.ag-root,
	&& .ag-layout-auto-height.ag-root-wrapper,
	&& .ag-layout-auto-height .ag-body-viewport {
		overflow-y: hidden;
	}

	&& .ag-layout-auto-height .ag-center-cols-container,
	&& .ag-layout-auto-height .ag-center-cols-viewport {
		min-height: 0;
	}

	&& .ag-row-last {
		border-bottom: none;
	}
`;

export function TalentReviewDepartmentsTable({ groups }: { groups: TalentReviewTableRow[] }) {
	const { t } = useTranslation();
	const [groupType, setGroupType] = useState<GroupTab>(GroupType.Department);

	const visible = useMemo(
		() => groups.filter((group) => group.type === groupType),
		[groups, groupType],
	);

	const columnDefs = useMemo<ColDef<TalentReviewTableRow>[]>(() => {
		const nameColumn: ColDef<TalentReviewTableRow> = {
			colId: 'name',
			headerName: t(TAB_LABEL_KEYS[groupType]),
			flex: 1,
			minWidth: 160,
			maxWidth: 280,
			valueGetter: ({ data }: ValueGetterParams<TalentReviewTableRow>) => data?.name ?? '',
		};
		const managerColumn: ColDef<TalentReviewTableRow> = {
			colId: 'manager',
			headerName: t('analytics.talentReview.departments.column.manager'),
			flex: 1,
			minWidth: 140,
			maxWidth: 220,
			valueGetter: ({ data }: ValueGetterParams<TalentReviewTableRow>) => data?.managerName ?? '',
		};
		const progressColumn: ColDef<TalentReviewTableRow> = {
			colId: 'progress',
			headerName: t('analytics.talentReview.departments.column.progress'),
			// Grows into the leftover width so the grid stays full-bleed while
			// the name columns stay capped and sit next to each other.
			flex: 1,
			minWidth: 180,
			initialSort: 'desc',
			valueGetter: ({ data }: ValueGetterParams<TalentReviewTableRow>) =>
				data ? ratio(data.placedCount, data.eligibleCount) : 0,
			cellRenderer: ({ data }: CustomCellRendererProps<TalentReviewTableRow>) =>
				data ? <GridProgressCell value={data.placedCount} total={data.eligibleCount} /> : null,
		};

		// A team has no primary manager, so that column only exists for departments.
		return groupType === GroupType.Department
			? [nameColumn, managerColumn, progressColumn]
			: [nameColumn, progressColumn];
	}, [t, groupType]);

	return (
		<Card>
			<Flex alignItems="center" justifyContent="space-between">
				<Heading3>{t('analytics.talentReview.departments.title')}</Heading3>
			</Flex>
			<Box mt={3}>
				<Tabs>
					<Flex ml={-3}>
						<Tabs.Item
							title={t(TAB_LABEL_KEYS[GroupType.Department])}
							selected={groupType === GroupType.Department}
							onClick={() => setGroupType(GroupType.Department)}
						/>
						<Tabs.Item
							title={t(TAB_LABEL_KEYS[GroupType.Team])}
							selected={groupType === GroupType.Team}
							onClick={() => setGroupType(GroupType.Team)}
						/>
					</Flex>
				</Tabs>
			</Box>
			<Box mt={3} mx={-3} mb={-3} minWidth={0}>
				<GridClip>
					<DataGrid<TalentReviewTableRow>
						rowData={visible}
						columnDefs={columnDefs}
						getRowId={({ data }: GetRowIdParams<TalentReviewTableRow>) => data.id}
						rowHeight={64}
						domLayout="autoHeight"
					/>
				</GridClip>
			</Box>
		</Card>
	);
}

TalentReviewDepartmentsTable.Skeleton = function TalentReviewDepartmentsTableSkeleton() {
	return (
		<Card>
			<Heading3.Skeleton width={220} />
			<Box mt={3} mx={-3} mb={-3} minWidth={0}>
				<DataGrid.Skeleton columns={3} rows={4} />
			</Box>
		</Card>
	);
};
